/*
 * File: withErrorBoundary.js
 * Path: src/components/ListPlaceStep1/
 * Purpose: HOC to add error boundary and debugging to ListPlaceStep1
 * Created: 2025-08-11
 * Dependencies: React, prop-types
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';

const withErrorBoundary = (WrappedComponent) => {
  return class ErrorBoundaryWrapper extends Component {
    static propTypes = {
      // Pass through all props to the wrapped component
      ...(WrappedComponent.propTypes || {})
    };

    state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };

    static getDerivedStateFromError(error) {
      return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
      console.error('Error in ListPlaceStep1:', error, errorInfo);
      this.setState({ errorInfo });
      
      // Log additional debug information
      console.log('Current props:', this.props);
      console.log('Current state:', this.state);
      
      // Log Redux state if available
      if (this.props.store) {
        console.log('Current Redux state:', this.props.store.getState());
      }
    }

    render() {
      if (this.state.hasError) {
        return (
          <div style={{ 
            padding: '20px', 
            border: '1px solid #f5c6cb', 
            backgroundColor: '#f8d7da',
            color: '#721c24',
            borderRadius: '4px',
            margin: '20px'
          }}>
            <h3>Something went wrong in the listing form</h3>
            <p>Please try refreshing the page. If the problem persists, contact support.</p>
            {process.env.NODE_ENV === 'development' && (
              <details style={{ marginTop: '10px' }}>
                <summary>Error details</summary>
                <pre style={{ 
                  whiteSpace: 'pre-wrap', 
                  backgroundColor: '#f1f1f1', 
                  padding: '10px',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflow: 'auto'
                }}>
                  {this.state.error && this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        );
      }

      return <WrappedComponent {...this.props} />;
    }
  };
};

export default withErrorBoundary;
